// Path: ./src/app/pages/ElasticTrigger/engagement/useEngagementIngest.ts
import { useCallback, useEffect, useRef, useState } from "react";
import {
  EngagementIngestRunResponseDto,
  EngagementStatusResponseDto,
} from "@swagger/typescript-fetch-goflyer";
// Undecorated on purpose: the decorated `adminApiService` swallows rejections
// and returns `undefined`, which would erase the run report and the server's
// error message. See the doc comment on `adminApiServiceRaw`.
import { adminApiServiceRaw } from "../../../../utils/api";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { EngagementApiError, toEngagementApiError } from "./errors";

export type EngagementRunKind = "ingest" | "backfill";

export type EngagementRunOutcome = {
  kind: EngagementRunKind;
  result?: EngagementIngestRunResponseDto;
  error?: EngagementApiError;
};

/** Idle cadence for /status. */
const STATUS_POLL_MS = 30_000;
/**
 * Faster cadence while a backfill is in flight: the point of watching is to see
 * `earliestBucket` walk backwards, which is the only progress signal a
 * synchronous backfill gives us.
 */
const ACTIVE_POLL_MS = 10_000;

/**
 * Client-side ceilings. Backfill is synchronous and runs one PostHog query per
 * `engagement.ingest.backfill_days`, so it can legitimately take minutes — this
 * is a runaway guard, not a deadline. Hitting it is reported as inconclusive.
 */
const RUN_TIMEOUT_MS: Record<EngagementRunKind, number> = {
  ingest: 3 * 60_000,
  backfill: 15 * 60_000,
};

export const useEngagementIngest = () => {
  const auth = useAuthHeaders();

  const [status, setStatus] = useState<EngagementStatusResponseDto | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [running, setRunning] = useState<EngagementRunKind | null>(null);
  const [lastOutcome, setLastOutcome] = useState<EngagementRunOutcome | null>(
    null,
  );

  const mounted = useRef(true);
  // Read synchronously in `run` to reject a double-trigger before React
  // re-renders with the new `running` state.
  const runningRef = useRef<EngagementRunKind | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchStatus = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent && mounted.current) {
        setStatusLoading(true);
      }
      try {
        const response =
          await adminApiServiceRaw.gfFlyerItemEngagementAdminControllerGetStatus(
            auth,
          );
        if (!mounted.current) return;
        setStatus(response);
        setStatusError(null);
      } catch (error) {
        const normalized = await toEngagementApiError(
          error,
          "Could not load engagement ingest status.",
        );
        if (!mounted.current) return;
        setStatusError(normalized.message);
      } finally {
        if (mounted.current) {
          setStatusLoading(false);
        }
      }
    },
    [auth],
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll for the main view, faster while a run is in flight.
  useEffect(() => {
    const interval = setInterval(
      () => fetchStatus({ silent: true }),
      running ? ACTIVE_POLL_MS : STATUS_POLL_MS,
    );
    return () => clearInterval(interval);
  }, [fetchStatus, running]);

  const run = useCallback(
    async (kind: EngagementRunKind) => {
      if (runningRef.current) return;
      runningRef.current = kind;
      setRunning(kind);
      setLastOutcome(null);

      const controller = new AbortController();
      const timeout = setTimeout(
        () => controller.abort(),
        RUN_TIMEOUT_MS[kind],
      );
      const options = { ...auth, signal: controller.signal };

      try {
        const result =
          kind === "backfill"
            ? await adminApiServiceRaw.gfFlyerItemEngagementAdminControllerRunBackfill(
                options,
              )
            : await adminApiServiceRaw.gfFlyerItemEngagementAdminControllerRunIngest(
                options,
              );
        if (mounted.current) {
          setLastOutcome({ kind, result });
        }
      } catch (error) {
        const normalized = await toEngagementApiError(
          error,
          kind === "backfill"
            ? "The backfill request failed."
            : "The ingest request failed.",
        );
        if (mounted.current) {
          setLastOutcome({ kind, error: normalized });
        }
      } finally {
        clearTimeout(timeout);
        runningRef.current = null;
        if (mounted.current) {
          setRunning(null);
        }
        // Always re-read /status, including after a failure: an inconclusive
        // backfill may well have written buckets before the gateway gave up.
        fetchStatus({ silent: true });
      }
    },
    [auth, fetchStatus],
  );

  const runIngest = useCallback(() => run("ingest"), [run]);
  const runBackfill = useCallback(() => run("backfill"), [run]);

  return {
    status,
    statusError,
    statusLoading,
    refreshStatus: fetchStatus,
    running,
    lastOutcome,
    dismissOutcome: useCallback(() => setLastOutcome(null), []),
    runIngest,
    runBackfill,
  };
};
